import { zodResolver } from '@hookform/resolvers/zod';
import { type AcceptInviteInput, type InviteDetails } from '@uit/shared/schemas/admin';
import {
  AcceptInviteInput as AcceptInviteInputSchema,
  InviteDetails as InviteDetailsSchema,
} from '@uit/shared/schemas/admin';
import { type AdminPublic } from '@uit/shared/schemas/auth';
import { AdminPublic as AdminPublicSchema } from '@uit/shared/schemas/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { apiMutation, useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import s from './AcceptInvitePage.module.scss';

interface FormValues {
  password: string;
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data, error, loading } = useApi<InviteDetails>(
    token ? `/api/auth/invite/${token}` : null,
    InviteDetailsSchema,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(AcceptInviteInputSchema.pick({ password: true })),
  });

  const onSubmit = async ({ password }: FormValues): Promise<void> => {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: AcceptInviteInput = { token, password };
      await apiMutation<AcceptInviteInput, AdminPublic>('/api/auth/accept-invite', payload, {
        schema: AdminPublicSchema,
      });
      await refetch();
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('404')) {
        setSubmitError('This invite link is invalid or expired.');
      } else if (message.includes('409')) {
        setSubmitError('An account with this email already exists.');
      } else {
        setSubmitError('Could not complete sign-up. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <div className={s.card}>
        <h1 className={s.title}>Set up your account</h1>
        <p className={s.subtitle}>ultimumIT portal — accept invitation</p>

        {loading && <div className={s.loading}>Loading invite…</div>}

        {error && (
          <div className={s.formError} role="alert">
            This invite link is invalid or expired.
          </div>
        )}

        {data && (
          <>
            <dl className={s.summary}>
              <dt>Email</dt>
              <dd>{data.email}</dd>
              <dt>Name</dt>
              <dd>{data.name}</dd>
              <dt>Role</dt>
              <dd>{data.role === 'admin' ? 'Admin (full access)' : 'Onboarding only'}</dd>
            </dl>

            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
              }}
              noValidate
            >
              {submitError && (
                <div className={s.formError} role="alert">
                  {submitError}
                </div>
              )}

              <div className={s.field}>
                <label className={s.label} htmlFor="password">
                  Choose a password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  className={s.input}
                  aria-invalid={!!errors.password}
                  {...register('password')}
                />
                {errors.password && <div className={s.fieldError}>{errors.password.message}</div>}
              </div>

              <button type="submit" className={s.button} disabled={submitting}>
                {submitting ? 'Setting up…' : 'Complete sign-up'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
